import { formatDuration } from '../lib/format'

export interface DonutSlice {
  id: string
  label: string
  value: number
  color: string
}

const SIZE = 120
const STROKE = 22
const RADIUS = (SIZE - STROKE) / 2
const CIRCUMFERENCE = 2 * Math.PI * RADIUS

/**
 * Hand-rolled rather than pulled from a chart library: it is one circle with
 * dash offsets, and the interaction a library would add is not wanted here.
 */
export function Donut({ slices, centre }: { slices: DonutSlice[]; centre?: string }) {
  const total = slices.reduce((sum, slice) => sum + slice.value, 0)
  if (total === 0) {
    return <p className="py-2 text-[13px] text-faint">Nothing tracked in this iteration.</p>
  }

  let offset = 0
  return (
    <div className="flex flex-wrap items-center gap-4">
      <svg
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        width={SIZE}
        height={SIZE}
        className="shrink-0 -rotate-90"
        role="img"
        aria-label="Time by category"
      >
        {slices.map((slice) => {
          const length = (slice.value / total) * CIRCUMFERENCE
          const circle = (
            <circle
              key={slice.id}
              cx={SIZE / 2}
              cy={SIZE / 2}
              r={RADIUS}
              fill="none"
              stroke={slice.color}
              strokeWidth={STROKE}
              strokeDasharray={`${length} ${CIRCUMFERENCE - length}`}
              strokeDashoffset={-offset}
            >
              <title>{`${slice.label} ${formatDuration(slice.value)}`}</title>
            </circle>
          )
          offset += length
          return circle
        })}
        {centre ? (
          <text
            x={SIZE / 2}
            y={SIZE / 2}
            textAnchor="middle"
            dominantBaseline="central"
            className="rotate-90 fill-[var(--tg-text)] text-[14px] font-semibold"
            style={{ transformOrigin: 'center' }}
          >
            {centre}
          </text>
        ) : null}
      </svg>

      <ul className="flex min-w-0 flex-1 flex-col gap-1">
        {slices.map((slice) => (
          <li key={slice.id} className="flex items-baseline gap-2 text-[12px]">
            <span
              className="size-2 shrink-0 translate-y-px rounded-[3px]"
              style={{ backgroundColor: slice.color }}
              aria-hidden="true"
            />
            <span className="min-w-0 flex-1 truncate text-dim">{slice.label}</span>
            <span className="shrink-0 text-faint tnum">{formatDuration(slice.value)}</span>
            <span className="w-9 shrink-0 text-right text-faint tnum">
              {Math.round((slice.value / total) * 100)}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}
